/**
 * Apartsales — useCart.
 *
 * Локальный persistent store для корзины пользователя — Set unitId.
 *
 * API:
 *   useCartCount()         → number               — для TopHeader badge
 *   useCartIds()           → string[]             — для /cart route (Tab 2)
 *   useCartItem(unitId)    → { inCart, toggle }   — для UnitCard/UnitDetail/PropertyDetail
 *
 * Хранится в localStorage под ключом 'apartsales:cart:v1' как
 * Record<unitId, true>. Запись `true` (не объект) — оставляем место под
 * дальнейшее расширение (lock-status, qty, добавлено-в) без миграции.
 *
 * Когда подключим backend (Workers + Supabase), этот hook расширится до
 * RPC (cart_add / cart_remove / cart_list), публичный API стабилен.
 *
 * SSR-safe (typeof window guard).
 */

import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'apartsales:cart:v1';
const CHANGE_EVENT = 'apartsales:cart:change';

type CartMap = Record<string, true>;

function readAll(): CartMap {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    // нормализуем — на случай если в legacy-storage оказались посторонние значения
    const out: CartMap = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (v) out[k] = true;
    }
    return out;
  } catch {
    return {};
  }
}

function writeAll(map: CartMap) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch {
    // private mode / quota — silent fallback
  }
}

/** Подписка на изменения cart-store (одна вкладка + cross-tab). */
function useCartSnapshot<T>(select: (map: CartMap) => T): T {
  const [value, setValue] = useState<T>(() => select(readAll()));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sync = () => setValue(select(readAll()));
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
    // select — стабильная функция из вызывающего useCartCount/useCartIds/useCartItem,
    // объявленная inline, поэтому не включаем в deps (иначе infinite re-subscribe).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return value;
}

/** Количество юнитов в корзине. Использует TopHeader для badge. */
export function useCartCount(): number {
  return useCartSnapshot((map) => Object.keys(map).length);
}

/** Список id юнитов в корзине. Использует /cart route. */
export function useCartIds(): string[] {
  return useCartSnapshot((map) => Object.keys(map));
}

/** Per-unit hook: `inCart` + `toggle`. Дроп-ин замена `useState(false)`. */
export function useCartItem(unitId: string): {
  inCart: boolean;
  toggle: () => boolean;
} {
  const inCart = useCartSnapshot((map) => Boolean(map[unitId]));

  const toggle = useCallback((): boolean => {
    const all = readAll();
    const next: boolean = !all[unitId];
    if (next) {
      all[unitId] = true;
    } else {
      delete all[unitId];
    }
    writeAll(all);
    return next;
  }, [unitId]);

  return { inCart, toggle };
}
